/**
 * CSV Utility Functions
 * Handles CSV template generation, download, and parsing for product incentive files.
 */

import type { ProductIncentiveData, CSVParseResult } from "@/feature/programs/domain/model";

/**
 * CSV headers for product incentive template
 */
const PRODUCT_INCENTIVE_HEADERS = [
  "Product ID",
  "Product Name",
  "Point Incentive",
];

/**
 * Sample data for CSV template
 */
const SAMPLE_TEMPLATE_DATA = [
  ["PRD001", "Product A", "100"],
  ["PRD002", "Product B", "250"],
];

/**
 * Generates and downloads a CSV template file for product incentive data.
 * Creates a template with headers and sample data rows.
 */
export const downloadProductIncentiveCsvTemplate = (): void => {
  const csvContent = [
    PRODUCT_INCENTIVE_HEADERS.join(","),
    ...SAMPLE_TEMPLATE_DATA.map((row) => row.join(",")),
  ].join("\n");

  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.setAttribute("href", url);
  link.setAttribute("download", "product_incentive_template.csv");
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

/**
 * Normalizes header strings for comparison.
 * Converts to lowercase and normalizes whitespace.
 */
const normalizeHeader = (header: string): string => {
  return header.trim().toLowerCase().replace(/\s+/g, " ");
};

/**
 * Finds the column index for a given header name.
 */
const getColumnIndex = (headers: string[], name: string): number => {
  const normalizedName = normalizeHeader(name);
  return headers.findIndex((h) => normalizeHeader(h) === normalizedName);
};

/**
 * Parses a CSV string and validates its content for product incentive data.
 * 
 * @param csvText - Raw CSV text content
 * @returns CSVParseResult with parsed data or error message
 * 
 * Validation rules:
 * - Must have all 5 required headers
 * - Must have at least one data row
 * - Product ID and Product Name cannot be empty
 * - Point Incentive must be a valid non-negative number
 */
export const parseProductIncentiveCsv = (csvText: string): CSVParseResult => {
  const lines = csvText
    .trim()
    .split("\n")
    .filter((line) => line.trim() !== "");

  // Check for minimum lines (header + at least 1 data row)
  if (lines.length < 2) {
    return {
      data: [],
      error: "Invalid CSV format: File must contain at least one product",
    };
  }

  const headers = lines[0].split(",").map((h) => h.trim());
  const normalizedHeaders = headers.map(normalizeHeader);

  // Expected headers (case-insensitive)
  const requiredHeaders = [
    "product id",
    "product name",
    "point incentive",
  ];

  // Check if all required headers are present
  const missingHeaders = requiredHeaders.filter(
    (required) => !normalizedHeaders.includes(required)
  );

  if (missingHeaders.length > 0) {
    return {
      data: [],
      error: "Invalid CSV format: Missing required headers",
    };
  }

  // Find column indices
  const productIdIdx = getColumnIndex(headers, "product id");
  const productNameIdx = getColumnIndex(headers, "product name");
  const pointIncentiveIdx = getColumnIndex(headers, "point incentive");

  // Parse and validate data rows
  const data: ProductIncentiveData[] = [];
  for (let i = 1; i < lines.length; i++) {
    const values = lines[i].split(",").map((v) => v.trim());
    const rowNum = i + 1; // 1-based row number for user-friendly error messages

    // Check if row has enough columns
    if (values.length < 5) {
      return {
        data: [],
        error: `Invalid CSV format: Missing required fields in row ${rowNum}`,
      };
    }

    const productId = values[productIdIdx] || "";
    const productName = values[productNameIdx] || "";
    const pointIncentiveStr = values[pointIncentiveIdx] || "";

    // Validate required fields are not empty
    if (!productId.trim()) {
      return {
        data: [],
        error: `Invalid CSV format: Missing required fields in row ${rowNum}`,
      };
    }
    if (!productName.trim()) {
      return {
        data: [],
        error: `Invalid CSV format: Missing required fields in row ${rowNum}`,
      };
    }

    // Validate Point Incentive is a valid positive number
    const pointIncentive = parseInt(pointIncentiveStr, 10);
    if (isNaN(pointIncentive) || pointIncentive < 0) {
      return {
        data: [],
        error: `Invalid CSV format: Point Incentive must be a valid number in row ${rowNum}`,
      };
    }

    data.push({
      productId,
      productName,
      pointIncentive,
    });
  }

  return { data, error: null };
};

